import { normalizeId } from "./frpHelpers";

export const hasManagementCompanyRole = (companies = []) =>
  companies.some((company) =>
    ["admin", "senior", "associate"].includes(company?.myRole)
  );

export const hasManagementCompanyWriteRole = (companies = []) =>
  companies.some((company) => ["admin", "senior"].includes(company?.myRole));

export const isManagerUser = (user, companies = []) =>
  user?.role === "A" ||
  user?.role === "M" ||
  user?.propertyRoles?.some((pr) => pr.role === "M") ||
  hasManagementCompanyRole(companies);

export const isTenantUser = (user) =>
  user?.role === "T" || user?.propertyRoles?.some((pr) => pr.role === "T");

export const isLandlordUser = (user) =>
  user?.role === "L" || user?.propertyRoles?.some((pr) => pr.role === "L");

const getCompanyById = (companies = [], companyId) => {
  if (!companyId) return null;
  const targetId = normalizeId(companyId);
  return companies.find((company) => normalizeId(company?._id) === targetId) || null;
};

const getCompanyRole = (companies = [], companyId) =>
  getCompanyById(companies, companyId)?.myRole || null;

const hasAssignedCompanyPropertyAccess = (property, companies = []) => {
  const propertyId = normalizeId(property?._id || property);
  const companyId = normalizeId(
    property?.managementCompany?._id || property?.managementCompany
  );
  const company = getCompanyById(companies, companyId);
  if (!company || company?.myRole !== "associate") return false;

  const assignedProperties = Array.isArray(company?.assignedProperties)
    ? company.assignedProperties
    : [];

  return assignedProperties.some(
    (assignedPropertyId) => normalizeId(assignedPropertyId) === propertyId
  );
};

export const canManageCompanyUser = (companyId, companies = []) => {
  const role = getCompanyRole(companies, companyId);
  return role === "admin" || role === "senior";
};

export const canManageManagementUser = (user, companies = []) =>
  user?.role === "A" ||
  user?.role === "M" ||
  user?.propertyRoles?.some((pr) => pr.role === "M") ||
  hasManagementCompanyWriteRole(companies);

export const canManagePropertyUser = (property, user, companies = []) => {
  if (!property || !user) return false;
  if (user?.role === "A") return true;
  if (user?.role === "M") return true;

  const propertyId = normalizeId(property?._id || property);
  const userId = normalizeId(user?._id || user?.id);
  const managerId = normalizeId(property?.manager?._id || property?.manager);

  if (managerId && managerId === userId) return true;
  if (
    user.propertyRoles?.some(
      (pr) => normalizeId(pr.property) === propertyId && pr.role === "M"
    )
  ) {
    return true;
  }

  const companyId = normalizeId(
    property?.managementCompany?._id || property?.managementCompany
  );
  return canManageCompanyUser(companyId, companies);
};

export const canUpdateWaterReadingUser = (property, user, companies = []) => {
  if (!property || !user) return false;
  if (canManagePropertyUser(property, user, companies)) return true;

  return hasAssignedCompanyPropertyAccess(property, companies);
};

export const canCaptureWaterPurchaseUser = (
  property,
  user,
  companies = [],
  { isTenantViewingOwnUnit = false } = {}
) => {
  if (!property || !user) return false;
  if (canUpdateWaterReadingUser(property, user, companies)) return true;
  return Boolean(isTenantViewingOwnUnit);
};

export const hasAnyTenantAccessUser = (user, myUnits = []) => {
  if (!user) return false;

  const equalId = (a, b) => {
    if (a == null || b == null) return false;
    return String(a) === String(b);
  };

  const hasActiveTenantRole = isTenantUser(user);
  const hasAnyUnits =
    Array.isArray(myUnits) &&
    myUnits.length > 0 &&
    myUnits.some(
      (unit) =>
        equalId(unit?.tenant?._id || unit?.tenant, user?._id) ||
        unit?.tenantHistory?.some((th) =>
          equalId(th?.tenant?._id || th?.tenant, user?._id)
        )
    );

  return hasActiveTenantRole || hasAnyUnits;
};

export const getUserRoleDisplayUser = (user, companies = [], myUnits = []) => {
  if (isManagerUser(user, companies)) return "Management";
  if (hasAnyTenantAccessUser(user, myUnits)) return "Tenant";
  if (isLandlordUser(user)) return "Landlord";
  return "Member";
};

export const getUserRolesUser = (user, companies = [], myUnits = []) => {
  if (!user) return [];
  const roles = [];
  if (isManagerUser(user, companies)) roles.push("manager");
  if (isLandlordUser(user)) roles.push("landlord");
  if (hasAnyTenantAccessUser(user, myUnits)) roles.push("tenant");
  return roles;
};
